import * as React from "react";
import InvoiceActivityStreamItems from "./InvoiceActivityStreamItems";
import {InvoiceEventType} from "../../../model/invoice/event/InvoiceEvent";
import ButtonSecondary from "../../../hiberUiKit/src/atoms/Buttons/ButtonSecondary";
import ActivityStreamUtils from "../../../utils/ActivityStreamUtils";
import AccountingNote from "../../../model/invoice/note/AccountingNote";
import Payment from "../../../model/payment/Payment";
import ActivityFilterDropDown from "./activityFilterDropDown/ActivityFilterDropDown";
import InvoiceEventTypeObject, {InvoiceEventCategory} from "../../../model/invoice/event/InvoiceEventTypeObject";
import deepEqual = require("deep-equal");

export type InvoiceActivityItemsType = InvoiceEventType | AccountingNote | Payment;

interface ComponentProps {
	/**
	 * Invoice identifier.
	 */
	invoiceId: string;
	/**
	 * Note id to set active class and focus on it.
	 */
	activeNoteId: string;
	/**
	 * Invoice activity data.
	 */
	events: InvoiceEventType[];
	/**
	 * Invoice notes.
	 */
	notes: AccountingNote[];
	/**
	 * Invoice payments.
	 */
	payments: Payment[];
	/**
	 * Edit note click action.
	 * @param note note to edit.
	 */
	onEditNoteClick: (note: AccountingNote) => void;
	/**
	 * Delete note success callback.
	 * @param note deleted note.
	 */
	onDeleteNoteSuccess: (note: AccountingNote) => void;
}

interface State {
	/**
	 * Number of events to be rendered.
	 */
	numberOfEventsToShow: number;
	/**
	 * Filtered (or not) activity data.
	 */
	filteredActivityData: InvoiceActivityItemsType[];
}

type Props = ComponentProps;

/**
 * Invoice activity stream component.
 */
export default class InvoiceActivityStream extends React.PureComponent<Props, State> {

	invoiceEventCategories = Object.keys(InvoiceEventCategory);

	constructor(props: Props) {
		super(props);
		this.state = {
			numberOfEventsToShow: 1,
			filteredActivityData: this.mergeActivityStreamItems(this.props.events, this.props.notes, this.props.payments)
		};
	}

	componentWillReceiveProps(newProps: Props) {
		if (!deepEqual(newProps.events, this.props.events)
			|| !deepEqual(newProps.notes, this.props.notes)
			|| !deepEqual(newProps.payments, this.props.payments)) {
			this.setState({
				filteredActivityData: this.mergeActivityStreamItems(newProps.events, newProps.notes, newProps.payments)
			});
		}
	}

	render() {
		const activityData = this.state.filteredActivityData;
		return (
			<div className="c-common-details_activity-stream-container">
				<ActivityFilterDropDown onSelect={this.filterSingleCategory}
										items={this.invoiceEventCategories}/>
				<InvoiceActivityStreamItems activityItems={activityData.slice(0, this.state.numberOfEventsToShow)}
											activeNoteId={this.props.activeNoteId}
											invoiceId={this.props.invoiceId}
											onEditNoteClick={this.props.onEditNoteClick}
											onDeleteNoteSuccess={this.props.onDeleteNoteSuccess}/>
				{this.state.numberOfEventsToShow < activityData.length &&
				<div className="c-common-details_activity-stream-container_show-more-button">
					<ButtonSecondary label={"Show more"}
									 onClick={() => this.setState({numberOfEventsToShow: this.state.numberOfEventsToShow + 5})}/>
				</div>}
			</div>
		)
	}

	/**
	 * Filter single category.
	 * @param {string} filter key of the category.
	 * @return {InvoiceActivityItemsType[]} Filtered data by key.
	 */
	filterSingleCategory = (filter: string) => {
		const activityData = this.mergeActivityStreamItems(this.props.events, this.props.notes, this.props.payments);
		const filteredActivityData = activityData.filter(ad =>
			InvoiceEventTypeObject.getTypesForCategory(filter)
				.map((ito: InvoiceEventTypeObject) => ito.id).indexOf(ad.type) > -1);
		this.setState({filteredActivityData});
	};

	/**
	 * Merge activity stream data and sort it by date - latest first.
	 * @param {InvoiceEventType[]} events invoice events.
	 * @param {AccountingNote[]} notes invoice notes.
	 * @param {Payment[]} payments invoice payments.
	 */
	mergeActivityStreamItems(events: Array<InvoiceEventType>, notes: AccountingNote[], payments: Payment[]) {
		return [].concat(events).concat(notes).concat(payments).sort(ActivityStreamUtils.sort);
	}
}
